import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * FilterPill — pill de filtre/segmentation unifiée (blog, cas clients, outils…).
 *
 * - Touch target ≥ 40px, focus ring explicite, cursor-pointer.
 * - `tone` contrôle le style actif : brand (conversion) ou ink (éditorial).
 * - `icon` optionnelle (lucide) pour les filtres riches (profils).
 */

export interface FilterPillProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  tone?: 'brand' | 'ink';
  icon?: LucideIcon;
}

export const FilterPill = forwardRef<HTMLButtonElement, FilterPillProps>(
  ({ active = false, tone = 'brand', icon: Icon, className, children, type = 'button', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={props.role ? undefined : active}
        className={cn(
          'inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold transition-all duration-150 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
          active
            ? tone === 'brand'
              ? 'border-brand-400 bg-brand-600 text-white shadow-cta-brand'
              : 'border-ink bg-ink text-ivory-50 shadow-cta-ink'
            : 'border-stone-200 bg-paper text-stone-600 hover:border-stone-300 hover:bg-stone-50 hover:text-ink',
          className,
        )}
        {...props}
      >
        {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />}
        {children}
      </button>
    );
  },
);
FilterPill.displayName = 'FilterPill';
